import constants from '../../../helpers/constants';
import chartsHelpers from '../include/charts/helpers';
import * as echarts from '../../../libs/echarts';

const helpers = {
  getLabelsByCalculationMode: calculationMode => {
    if (calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE) {
      return ['report_progress_type_baseline_progress', 'report_progress_type_actual_progress'];
    }

    if (calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE) {
      return ['report_progress_type_planned_progress', 'report_progress_type_baseline_progress'];
    }

    return ['report_progress_type_planned_progress', 'report_progress_type_actual_progress'];
  },
};

export default {
  monthProgressChart: calculationMode => ({
    default: {
      tooltip: {
        show: true,
        trigger: 'axis',
        axisPointer: {
          lineStyle: { color: '#FF3434' },
        },
        borderColor: '#808080',
        borderWidth: 1,
        borderRadius: 4,
        padding: 8,
        backgroundColor: '#FFFFFF',
        extraCssText: 'box-shadow: 0px 9px 22px -2px rgba(0, 0, 0, 0.2);',
        formatter: params => {
          const labels = helpers.getLabelsByCalculationMode(calculationMode);
          const firstData = params.find(item => item.seriesName === 'progress_line_1');
          const secData = params.find(item => item.seriesName === 'progress_line_2');
          const name = firstData?.name || secData?.name;
          const values = name.split(' ');

          let variance = firstData && secData && (secData.value - firstData.value);

          if (calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE) {
            variance = firstData && secData && (firstData.value - secData.value);
          }

          return `<div class="chart_tooltip">
            ${name ? `<div class="tooltip_title">${__(`${values[0]}_full`)} ${values[1]}</div>` : ''}
            ${firstData ? `<div class="tooltip_info">
                  <div class="marker">${firstData.marker}</div>
                  <div class="name">${__(`${labels[0]}_tooltip`)}</div>
                  <div class="value">${firstData.value}%</div>
                </div>` : ''}
            ${secData ? `<div class="tooltip_info">
                  <div class="marker">${secData.marker}</div>
                  <div class="name">${__(`${labels[1]}_tooltip`)}</div>
                  <div class="value">${secData.value}%</div>
                </div>` : ''}
            ${firstData && secData ? `<div class="tooltip_info">
                  <div class="name">${__('report_page_variance')}</div>
                  <div class="value">${variance}%</div>
                </div>` : ''}
          </div>
        </div>`;
        },
      },
      xAxis: {
        type: 'category',
        boundaryGap: true,
        data: [],
        axisLabel: {
          show: true,
          formatter: value => {
            const values = value.split(' ');

            return __(`${values[0]}`);
          },
          color: '#191919',
          fontSize: 16,
          lineHeight: 19,
          fontFamily: 'Lato-Regular, sans-serif',
          padding: [19, 0, 0, 0],
        },
      },
      yAxis: {
        type: 'value',
        min() {
          return 0;
        },
        max() {
          return 100;
        },
        splitNumber: 6,
        interval: 20,
        axisLabel: {
          show: true,
          formatter: '{value}%',
          color: '#999999',
          fontSize: 12,
          lineHeight: 15,
          fontFamily: 'Lato-Regular, sans-serif',
        },
      },
      series: [
        {
          name: 'progress_line_1',
          label: () => helpers.getLabelsByCalculationMode(calculationMode)[0],
          type: 'line',
          colorBy: 'series',
          color: '#8BC34A',
          disabledColor: '#999999',
          data: [],
        },
        {
          name: 'progress_line_2',
          label: () => helpers.getLabelsByCalculationMode(calculationMode)[1],
          type: 'line',
          colorBy: 'series',
          color: '#4484CD',
          disabledColor: '#CCCCCC',
          data: [],
        },
      ],
      legend: {
        show: false,
      },
      silent: false,
      animation: true,
    },

    stub: {
      tooltip: {
        show: true,
        trigger: 'axis',
      },
      xAxis: {
        type: 'category',
        boundaryGap: true,
        data: [1, 2, 3, 4, 5, 6],
        axisLabel: { show: false },
      },
      yAxis: {
        type: 'value',
        min() {
          return 0;
        },
        max() {
          return 100;
        },
        splitNumber: 6,
        interval: 20,
        axisLabel: { show: false },
      },
      series: [
        {
          name: 'progress_line_1',
          label: () => '-',
          type: 'line',
          colorBy: 'series',
          color: '#999999',
          data: [15, 40, 50, 78, 78, 100],
        },
        {
          name: 'progress_line_2',
          label: () => '-',
          type: 'line',
          colorBy: 'series',
          color: '#CCCCCC',
          data: [20, 30, 40, 50, 60, 60],
        },
      ],
      legend: {
        show: false,
      },
      silent: true,
      animation: false,
    },
  }),

  scheduleProgressChart: {
    default: {
      grid: { left: 250 },
      tooltip: {
        show: true,
        trigger: 'axis',
        borderColor: '#808080',
        borderWidth: 1,
        borderRadius: 4,
        padding: 8,
        backgroundColor: '#FFFFFF',
        extraCssText: 'box-shadow: 0px 9px 22px -2px rgba(0, 0, 0, 0.2);',
        formatter: params => {
          const data = params[0].data;
          const progressCustomData = data.progressData;
          const realValue = data.realValue ?? data.value;

          return `<div class="chart_tooltip">
          <div class="tooltip_title">${progressCustomData.projectName}</div>
            ${!progressCustomData.isSingleProjectReport ? ` <div class="tooltip_info">
                <div class="name">${__('reports_progress_actual')}</div>
                <div class="value">${data.progressData.progressValue_1}%</div>
              </div>` : ''}
            ${!progressCustomData.isSingleProjectReport ? `<div class="tooltip_info">
              <div class="name">${__('reports_progress_planned')}</div>
              <div class="value">${data.progressData.progressValue_2}%</div>
            </div>` : ''}
            <div class="tooltip_info">
              <div class="name">${progressCustomData.isSingleProjectReport ? progressCustomData?.name : __('reports_progress_variance')}</div>
              <div class="value">${realValue}%</div>
            </div>
          </div>
        </div>`;
        },
        axisPointer: {
          type: 'shadow',
          z: 0,
        },
      },
      xAxis: {
        type: 'value',
        position: 'top',
        splitLine: {
          lineStyle: {
            type: 'dashed',
          },
        },
        interval: 25,
        min: -100,
        max: 100,
        axisLabel: {
          show: true,
          formatter: '{value}%',
          color: '#999999',
          fontSize: 12,
          lineHeight: 15,
          fontFamily: 'Lato-Regular, sans-serif',
        },
      },
      yAxis: {
        type: 'category',
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: {
          show: true,
          color: '#191919',
          align: 'left',
          fontSize: 16,
          lineHeight: 19,
          width: 200,
          margin: 205,
          overflow: 'truncate',
          fontFamily: 'Lato-Regular, sans-serif',
          formatter: value => {
            const chartInstance = echarts.getInstanceByDom(document.querySelector('[_echarts_instance_]'));
            const options = chartInstance.getOption();
            const isSingleProject = options.series[0].data[0].progressData.isSingleProjectReport;
            const fontOption = isSingleProject ? '16px Lato' : '14px Lato';

            const truncated = chartsHelpers.truncateTextToTwoLinesWithNewline(value, 170, fontOption);

            return `${truncated}`;
          },
        },
        data: [],
      },
      series: [{
        name: 'chart',
        type: 'bar',
        colorBy: 'series',
        barWidth: '40%',
        barMaxWidth: 45,
        barMinHeight: 0.5,
        itemStyle: { borderRadius: 4 },
        label: {
          show: true,
          position: 'inside',
          // formatter: params => `${params.value}%`,
          formatter: params => {
            const realValue = params.data.realValue ?? params.value;

            return `${realValue}%`;
          },
          color: '#FFFFFF',
          fontSize: 12,
          lineHeight: 14,
          fontFamily: 'Lato-Bold, sans-serif',
        },
        labelLayout: params => ({ fontSize: (params.rect.height < 14 || (params.rect.width < 30 && params.rect.width !== 0.5)) ? 0 : 12 }),
        data: [],
        animation: true,
        silent: false,
      }],
      animation: true,
    },

    stub: {
      grid: { left: 250 },
      tooltip: { show: false },
      xAxis: {
        type: 'value',
        position: 'top',
        splitLine: {
          lineStyle: {
            type: 'dashed',
          },
        },
        interval: 25,
        min: -100,
        max: 100,
        axisLabel: {
          show: true,
          formatter: '{value}%',
          color: '#999999',
          fontSize: 12,
          lineHeight: 15,
          fontFamily: 'Lato-Regular, sans-serif',
        },
      },
      yAxis: {
        type: 'category',
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        data: [1, 2, 3, 4],
      },
      series: [{
        name: 'chart',
        type: 'bar',
        colorBy: 'series',
        barWidth: 40,
        itemStyle: {
          color: params => {
            if (params.data.value < 0) {
              return '#CCCCCC';
            }

            return '#E9E9E9';
          },
          borderRadius: 4,
        },
        label: { show: false },
        data: [
          { value: -45 },
          { value: 77 },
          { value: 77 },
          { value: 55 },
        ],
        animation: false,
        silent: true,
      }],
      animation: false,
    },
  },
};
